import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreateRocketSchema, type RocketRecord, type UpdateRocket, type VehiclePerformanceDto } from '@trajectory/contracts';
import type { z } from 'zod';
import { apiFetch } from '@/lib/api';

/** See the matching comment in `useMissions.ts` — `z.input` keeps defaulted fields optional for callers. */
type NewRocket = z.input<typeof CreateRocketSchema>;

export function useRocketList() {
  return useQuery({
    queryKey: ['rockets'],
    queryFn: () => apiFetch<RocketRecord[]>('/rockets'),
  });
}

export function useRocket(id: string | null) {
  return useQuery({
    queryKey: ['rocket', id],
    queryFn: () => apiFetch<RocketRecord>(`/rockets/${id}`),
    enabled: id !== null,
  });
}

export function useRocketPerformance(id: string | null) {
  return useQuery({
    queryKey: ['rocket-performance', id],
    queryFn: () => apiFetch<VehiclePerformanceDto>(`/rockets/${id}/performance`),
    enabled: id !== null,
  });
}

export function useCreateRocket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: NewRocket) => apiFetch<RocketRecord>('/rockets', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rockets'] }),
  });
}

export function useDeleteRocket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) =>
      apiFetch<void>(`/rockets/${id}`, { method: 'DELETE', headers: { 'If-Match': `"${version}"` } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rockets'] }),
  });
}

/**
 * Used by the autosave hook: PATCHes with `If-Match` so a stale write
 * surfaces as a 412 rather than silently clobbering. Also writes the saved
 * record into the query cache and invalidates the now-outdated performance
 * figures — see the matching comment on `useSaveMission` for why this
 * matters: without it, a later re-selection of the same rocket from a
 * stale list would revert every edit made since that list was fetched.
 */
export function useSaveRocket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version, patch }: { id: string; version: number; patch: UpdateRocket }) =>
      apiFetch<RocketRecord>(`/rockets/${id}`, {
        method: 'PATCH',
        headers: { 'If-Match': `"${version}"` },
        body: JSON.stringify(patch),
      }),
    onSuccess: (saved) => {
      queryClient.setQueryData(['rocket', saved.id], saved);
      queryClient.setQueryData<RocketRecord[]>(['rockets'], (current) =>
        current?.map((item) => (item.id === saved.id ? saved : item)),
      );
      queryClient.invalidateQueries({ queryKey: ['rocket-performance', saved.id] });
    },
  });
}
